import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords, useHmsConfig } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   NICU / NEONATAL MODULE — Complete Newborn Care
   - Birth Registration & Apgar Scoring
   - Incubator / Warmer Allocation
   - Weight Monitoring Chart
   - Feeding Record (Breast / Formula / NG)
   - Bilirubin / Phototherapy Tracking
   - CPAP / Ventilator Parameters
   - Neonatal Screening (NBS)
   - Discharge Criteria Checklist
═══════════════════════════════════════════════════════════ */

const APGAR_ITEMS = [
  { criterion: 'Heart Rate', scores: ['Absent (0)', '<100 bpm (1)', '>100 bpm (2)'] },
  { criterion: 'Respiratory Effort', scores: ['Absent (0)', 'Weak/Irregular (1)', 'Crying (2)'] },
  { criterion: 'Muscle Tone', scores: ['Limp (0)', 'Some Flexion (1)', 'Active Motion (2)'] },
  { criterion: 'Reflex Response', scores: ['None (0)', 'Grimace (1)', 'Cry/Cough/Sneeze (2)'] },
  { criterion: 'Colour', scores: ['Blue/Pale (0)', 'Blue Extremities (1)', 'Pink All Over (2)'] },
];

function IncubatorsSetup({ onSave, busy }) {
  const [rows, setRows] = useState([{ id: 'INC-01', type: 'Closed Incubator' }]);
  return (
    <div className="card mb" style={{ borderLeft: '4px solid #10b981' }}>
      <h3 style={{ marginTop: 0, color: '#10b981' }}>Configure Incubators / Warmers</h3>
      <p className="hint mb">No incubators configured. Add units before allocating neonates.</p>
      {rows.map((r, i) => (
        <div key={i} className="form-grid mb">
          <div className="field"><label>Unit ID</label><input className="input" value={r.id} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, id: e.target.value } : x))} /></div>
          <div className="field"><label>Type</label><select className="input" value={r.type} onChange={(e) => setRows((list) => list.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}><option>Closed Incubator</option><option>Radiant Warmer</option><option>Open Care System</option></select></div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRows((list) => [...list, { id: `INC-${String(list.length + 1).padStart(2, '0')}`, type: 'Closed Incubator' }])}>+ Add Unit</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onSave(rows.map((r) => ({ ...r, status: 'Available', baby: null, temp: null, humidity: null })))}>{busy ? 'Saving…' : '✓ Save Units'}</button>
      </div>
    </div>
  );
}

function printApgarReport({ neonate, scores, total, apgarLabel }) {
  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const win = window.open('', '_blank', 'width=700,height=540');
  if (!win) return;
  win.document.write('<html><head><title>Apgar Score</title><style>body{font-family:Arial,sans-serif;max-width:620px;margin:28px auto}table{width:100%;border-collapse:collapse;margin:14px 0}th,td{padding:8px 10px;border:1px solid #e2e8f0;text-align:left;font-size:12px}th{background:#f1f5f9;font-weight:700}.score-box{margin:20px 0;padding:16px;border:2px solid #94a3b8;border-radius:8px;text-align:center}.big{font-size:40px;font-weight:900}@media print{body{margin:8px}}</style></head><body>');
  win.document.write('<h2 style="margin-bottom:4px">Apgar Score Report</h2>');
  win.document.write('<p style="font-size:12px;color:#64748b">Printed: ' + new Date().toLocaleString('en-IN') + '</p>');
  win.document.write('<p><strong>Baby:</strong> ' + esc(neonate?.babyName || '—') + ' &nbsp;|&nbsp; <strong>Mother:</strong> ' + esc(neonate?.motherName || '—') + '</p>');
  win.document.write('<p><strong>DOB:</strong> ' + esc(neonate?.dob || '—') + ' ' + esc(neonate?.tob || '') + ' &nbsp;|&nbsp; <strong>Gestation:</strong> ' + esc(String(neonate?.gestationWeeks || '—')) + ' wks &nbsp;|&nbsp; <strong>Delivery:</strong> ' + esc(neonate?.deliveryType || '—') + '</p>');
  win.document.write('<table><thead><tr><th>Criterion</th><th>Score</th><th>Description</th></tr></thead><tbody>');
  APGAR_ITEMS.forEach((item, idx) => {
    win.document.write('<tr><td>' + esc(item.criterion) + '</td><td style="font-weight:700;text-align:center">' + scores[idx] + '</td><td>' + esc(item.scores[scores[idx]]) + '</td></tr>');
  });
  win.document.write('</tbody></table>');
  const color = total >= 7 ? '#16a34a' : total >= 4 ? '#b45309' : '#dc2626';
  win.document.write('<div class="score-box"><div class="big" style="color:' + color + '">' + total + '/10</div><div style="font-weight:700;font-size:15px;color:' + color + '">' + esc(apgarLabel) + '</div></div>');
  win.document.write('<p style="font-size:11px;color:#64748b">Paediatrician: ' + esc(neonate?.paediatrician || '—') + '</p>');
  win.document.write('</body></html>');
  win.document.close();
  win.print();
}

function ApgarCalculator({ neonate, onSave }) {
  const [scores, setScores] = useState({ 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const total = Object.values(scores).reduce((s, v) => s + v, 0);
  const apgarColor = total >= 7 ? '#10b981' : total >= 4 ? '#f59e0b' : '#dc2626';
  const apgarLabel = total >= 7 ? 'Normal' : total >= 4 ? 'Requires Assistance' : 'Immediate Resuscitation';

  return (
    <div className="card" style={{ borderLeft: '4px solid #6366f1' }}>
      <h3 style={{ marginTop: 0, color: '#6366f1' }}>📊 Apgar Score Calculator</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {APGAR_ITEMS.map((item, idx) => (
          <div key={idx} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>{item.criterion}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {item.scores.map((label, val) => (
                <button key={val} type="button" onClick={() => setScores(s => ({ ...s, [idx]: val }))} style={{ padding: '4px 12px', borderRadius: 8, border: '2px solid', borderColor: scores[idx] === val ? '#6366f1' : '#e2e8f0', background: scores[idx] === val ? '#6366f1' : '#fff', color: scores[idx] === val ? '#fff' : '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>{label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: '16px 20px', background: `${apgarColor}15`, border: `2px solid ${apgarColor}40`, borderRadius: 14, display: 'flex', gap: 20, alignItems: 'center' }}>
        <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 48, fontWeight: 900, color: apgarColor, lineHeight: 1 }}>{total}</div>
        <div>
          <div style={{ fontWeight: 900, fontSize: 16, color: apgarColor }}>{apgarLabel}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Score: {total}/10 · {total >= 7 ? 'No intervention needed' : total >= 4 ? 'Stimulation + Supplemental O₂' : '⚠ Positive pressure ventilation required'}</div>
        </div>
      </div>
      {neonate && onSave && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {msg && <div className="info-banner mb" style={{ width: '100%' }}>{msg}</div>}
          <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={async () => {
            setSaving(true);
            setMsg('');
            try {
              await onSave({ apgarCalcScore: total, apgarCalcAt: new Date().toISOString(), apgarBreakdown: scores });
              setMsg(`Apgar ${total}/10 saved to ${neonate.babyName}.`);
            } catch (e) {
              setMsg(e.message || 'Could not save Apgar score');
            } finally { setSaving(false); }
          }}>{saving ? 'Saving…' : `✓ Save to ${neonate.babyName || 'selected neonate'}`}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => printApgarReport({ neonate, scores, total, apgarLabel })}>Print Apgar</button>
        </div>
      )}
      {!neonate && <p className="hint" style={{ marginTop: 10 }}>Select a neonate from NICU Census to save this score.</p>}
    </div>
  );
}

function WeightChartTab({ neonate, onSaveWeights }) {
  const [weights, setWeights] = useState(neonate?.weights || [
    { day: 'Birth', date: neonate?.dob || '', weight: neonate?.birthWeightGrams || 2450, feed: 'Nil', urine: false, stool: false },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (neonate?.weights?.length) setWeights(neonate.weights);
    else if (neonate) {
      setWeights([{ day: 'Birth', date: neonate.dob || '', weight: neonate.birthWeightGrams || 2450, feed: 'Nil', urine: false, stool: false }]);
    }
  }, [neonate]);
  const birthWeight = weights[0]?.weight || 2450;
  const latestWeight = weights[weights.length - 1]?.weight || 2450;
  const weightChange = latestWeight - birthWeight;
  const pctChange = ((weightChange / birthWeight) * 100).toFixed(1);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 12 }}>
        {[{ label: 'Birth Weight', val: `${birthWeight}g`, color: '#6366f1' }, { label: 'Current Weight', val: `${latestWeight}g`, color: '#10b981' }, { label: 'Weight Change', val: `${weightChange > 0 ? '+' : ''}${weightChange}g (${pctChange}%)`, color: weightChange >= 0 ? '#10b981' : '#f97316' }, { label: 'Days', val: weights.length - 1, color: '#3b82f6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 16, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>📈 Weight & Feeding Chart</h3>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setWeights(w => [...w, { day: `Day ${w.length}`, date: '', weight: latestWeight, feed: '', urine: false, stool: false }])}>+ Add Reading</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={saving || !onSaveWeights} onClick={async () => {
            if (!onSaveWeights) return;
            setSaving(true);
            try { await onSaveWeights(weights); } finally { setSaving(false); }
          }}>{saving ? 'Saving…' : '✓ Save Chart'}</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Day</th><th>Weight (g)</th><th>Change</th><th>Feeding</th><th>Urine</th><th>Stool</th><th>Bilirubin</th></tr></thead>
            <tbody>
              {weights.map((w, i) => {
                const prev = weights[i - 1]?.weight;
                const change = prev ? w.weight - prev : 0;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{w.day}</td>
                    <td><input type="number" className="input" style={{ width: 80, padding: '2px 8px', fontSize: 11, fontWeight: 700 }} value={w.weight} onChange={e => setWeights(arr => arr.map((x, j) => j === i ? { ...x, weight: Number(e.target.value) || 0 } : x))} /></td>
                    <td style={{ fontWeight: 700, color: i === 0 ? '#94a3b8' : change >= 0 ? '#10b981' : '#f97316' }}>{i === 0 ? 'Baseline' : `${change > 0 ? '+' : ''}${change}g`}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}><input className="input" style={{ width: 140, padding: '2px 8px', fontSize: 11 }} value={w.feed} onChange={e => setWeights(arr => arr.map((x, j) => j === i ? { ...x, feed: e.target.value } : x))} /></td>
                    <td style={{ textAlign: 'center' }}><button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 14, padding: '2px 8px' }} onClick={() => setWeights(arr => arr.map((x, j) => j === i ? { ...x, urine: !x.urine } : x))}>{w.urine ? '💧' : '—'}</button></td>
                    <td style={{ textAlign: 'center' }}><button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 14, padding: '2px 8px' }} onClick={() => setWeights(arr => arr.map((x, j) => j === i ? { ...x, stool: !x.stool } : x))}>{w.stool ? '✓' : '—'}</button></td>
                    <td><input type="number" step="0.1" className="input" style={{ width: 70, padding: '2px 8px', fontSize: 11 }} placeholder="mg/dL" value={w.bilirubin ?? ''} onChange={e => setWeights(arr => arr.map((x, j) => j === i ? { ...x, bilirubin: e.target.value } : x))} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const NBS_TESTS = [
  'TSH (Congenital Hypothyroidism)',
  'Hearing Screen (OAE/AABR)',
  'Metabolic Panel (IEM)',
  'G6PD Deficiency',
  'Galactosemia',
  'CAH (Congenital Adrenal Hyperplasia)',
  'PKU (Phenylketonuria)',
  'Sickle Cell Disease',
];

const DISCHARGE_CRITERIA = [
  'Weight gain adequate (≥25 g/day for 3 consecutive days)',
  'Temperature stable (36.5–37.5°C) in open cot',
  'Feeding well established (breastfeed or bottle)',
  'No active jaundice requiring phototherapy',
  'No active respiratory distress or apnoea',
  'BCG & Hepatitis B vaccinations given',
  'Hearing screen performed',
  'Family counselling completed',
  'Follow-up appointment scheduled',
];

function PhototherapyTab({ neonate, onSave }) {
  const [billies, setBillies] = useState(neonate?.phBiliValues || []);
  const [lampHours, setLampHours] = useState(neonate?.lampHoursTotal || 0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (neonate) {
      setLampHours(neonate.lampHoursTotal || 0);
      setBillies(neonate.phBiliValues || []);
    }
  }, [neonate?.recordId]);

  const active = !!neonate?.phototherapyActive;

  async function togglePt() {
    if (!neonate || !onSave) return;
    setSaving(true);
    const now = new Date().toISOString();
    const patch = active
      ? { phototherapyActive: false, phototherapyStoppedAt: now }
      : { phototherapyActive: true, phototherapyStartedAt: now, phototherapyStoppedAt: null };
    try {
      await onSave(patch);
      setMsg(active ? 'Phototherapy stopped.' : 'Phototherapy started.');
    } catch (e) {
      setMsg(e.message || 'Could not update');
    } finally { setSaving(false); }
  }

  async function saveBili() {
    if (!neonate || !onSave) return;
    setSaving(true);
    try {
      await onSave({ phBiliValues: billies, lampHoursTotal: lampHours });
      setMsg('Readings saved.');
    } catch (e) {
      setMsg(e.message || 'Could not save');
    } finally { setSaving(false); }
  }

  return (
    <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
      <h3 style={{ marginTop: 0, color: '#f59e0b' }}>☀ Phototherapy</h3>
      {!neonate && <p className="hint">Select a neonate from NICU Census.</p>}
      {neonate && (
        <>
          {msg && <div className="info-banner mb">{msg}</div>}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ padding: '10px 18px', borderRadius: 10, background: active ? '#fffbeb' : '#f0fdf4', border: `2px solid ${active ? '#f59e0b' : '#86efac'}`, fontWeight: 800, fontSize: 15, color: active ? '#92400e' : '#16a34a' }}>
              {active ? '☀ ACTIVE' : '○ Inactive'}
            </div>
            {neonate.phototherapyStartedAt && (
              <span style={{ fontSize: 12, color: '#64748b' }}>Started: {new Date(neonate.phototherapyStartedAt).toLocaleString('en-IN')}</span>
            )}
            {neonate.phototherapyStoppedAt && !active && (
              <span style={{ fontSize: 12, color: '#64748b' }}>Stopped: {new Date(neonate.phototherapyStoppedAt).toLocaleString('en-IN')}</span>
            )}
            <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={togglePt}>{saving ? '…' : active ? 'Stop Phototherapy' : 'Start Phototherapy'}</button>
          </div>
          <div className="field mb">
            <label>Lamp hours total</label>
            <input type="number" className="input" style={{ width: 120 }} value={lampHours} onChange={(e) => setLampHours(Number(e.target.value) || 0)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <strong style={{ fontSize: 13 }}>Bilirubin Readings</strong>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBillies((b) => [...b, { date: new Date().toISOString().slice(0, 10), value: '', notes: '' }])}>+ Add reading</button>
          </div>
          <div className="table-wrap">
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Date</th><th>Bilirubin (mg/dL)</th><th>Notes</th></tr></thead>
              <tbody>
                {billies.map((b, i) => (
                  <tr key={i}>
                    <td><input type="date" className="input" style={{ fontSize: 11, padding: '2px 6px' }} value={b.date || ''} onChange={(e) => setBillies((arr) => arr.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} /></td>
                    <td><input type="number" step="0.1" className="input" style={{ width: 80, fontSize: 11, padding: '2px 6px' }} value={b.value || ''} onChange={(e) => setBillies((arr) => arr.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} /></td>
                    <td><input className="input" style={{ fontSize: 11, padding: '2px 6px' }} value={b.notes || ''} onChange={(e) => setBillies((arr) => arr.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))} /></td>
                  </tr>
                ))}
                {billies.length === 0 && <tr><td colSpan={3} className="hint table-empty">No readings yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="form-actions" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={saveBili}>{saving ? 'Saving…' : 'Save readings'}</button>
          </div>
        </>
      )}
    </div>
  );
}

function NbsScreeningTab({ neonate, onSave }) {
  const initScreens = () => {
    const s = {};
    NBS_TESTS.forEach((t) => { s[t] = { done: false, doneDate: '', result: '' }; });
    return { ...s, ...(neonate?.nbsScreening || {}) };
  };
  const [screens, setScreens] = useState(initScreens);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const s = {};
    NBS_TESTS.forEach((t) => { s[t] = { done: false, doneDate: '', result: '' }; });
    setScreens({ ...s, ...(neonate?.nbsScreening || {}) });
  }, [neonate?.recordId]);

  async function save() {
    if (!neonate || !onSave) return;
    setSaving(true);
    try {
      await onSave({ nbsScreening: screens });
      setMsg('NBS screening saved.');
    } catch (e) {
      setMsg(e.message || 'Could not save');
    } finally { setSaving(false); }
  }

  const doneCount = NBS_TESTS.filter((t) => screens[t]?.done).length;

  return (
    <div className="card" style={{ borderLeft: '4px solid #3b82f6' }}>
      <h3 style={{ marginTop: 0, color: '#3b82f6' }}>🧪 NBS — Newborn Screening</h3>
      {!neonate && <p className="hint">Select a neonate from NICU Census.</p>}
      {neonate && (
        <>
          {msg && <div className="info-banner mb">{msg}</div>}
          <p className="hint mb">{doneCount}/{NBS_TESTS.length} screens done for {neonate.babyName}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {NBS_TESTS.map((test) => {
              const sc = screens[test] || {};
              return (
                <div key={test} style={{ display: 'grid', gridTemplateColumns: '24px 2fr 130px 1fr', gap: 8, alignItems: 'center', padding: '8px 12px', background: sc.done ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: `1px solid ${sc.done ? '#86efac' : '#e2e8f0'}` }}>
                  <input
                    type="checkbox"
                    checked={sc.done || false}
                    onChange={(e) => setScreens((s) => ({ ...s, [test]: { ...sc, done: e.target.checked, doneDate: e.target.checked ? (sc.doneDate || new Date().toISOString().slice(0, 10)) : sc.doneDate } }))}
                  />
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{test}</span>
                  <input type="date" className="input" style={{ fontSize: 11, padding: '2px 6px' }} value={sc.doneDate || ''} onChange={(e) => setScreens((s) => ({ ...s, [test]: { ...sc, doneDate: e.target.value } }))} />
                  <input className="input" placeholder="Result" style={{ fontSize: 11, padding: '2px 6px' }} value={sc.result || ''} onChange={(e) => setScreens((s) => ({ ...s, [test]: { ...sc, result: e.target.value } }))} />
                </div>
              );
            })}
          </div>
          <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save NBS'}</button>
        </>
      )}
    </div>
  );
}

function DischargeChecklistTab({ neonate, incubators, onSave, onSaveIncubators }) {
  const initChecks = () => {
    const c = {};
    DISCHARGE_CRITERIA.forEach((k) => { c[k] = false; });
    return { ...c, ...(neonate?.dischargeChecks || {}) };
  };
  const [checks, setChecks] = useState(initChecks);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const c = {};
    DISCHARGE_CRITERIA.forEach((k) => { c[k] = false; });
    setChecks({ ...c, ...(neonate?.dischargeChecks || {}) });
  }, [neonate?.recordId]);

  const allChecked = DISCHARGE_CRITERIA.every((k) => checks[k]);
  const doneCount = DISCHARGE_CRITERIA.filter((k) => checks[k]).length;

  async function save() {
    if (!neonate || !onSave) return;
    setSaving(true);
    try {
      await onSave({ dischargeChecks: checks });
      setMsg('Checklist saved.');
    } catch (e) {
      setMsg(e.message || 'Could not save');
    } finally { setSaving(false); }
  }

  async function discharge() {
    if (!neonate || !onSave) return;
    if (!window.confirm(`Discharge ${neonate.babyName}? Incubator will be freed.`)) return;
    setSaving(true);
    try {
      await onSave({ dischargeChecks: checks, status: 'Discharged', dischargedAt: new Date().toISOString() });
      if (neonate.incubator && onSaveIncubators) {
        const next = (incubators || []).map((inc) => inc.id === neonate.incubator ? { ...inc, status: 'Available', baby: null } : inc);
        await onSaveIncubators({ incubators: next });
      }
      setMsg(`${neonate.babyName} discharged.`);
    } catch (e) {
      setMsg(e.message || 'Could not discharge');
    } finally { setSaving(false); }
  }

  return (
    <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
      <h3 style={{ marginTop: 0, color: '#10b981' }}>✅ Discharge Checklist</h3>
      {!neonate && <p className="hint">Select a neonate from NICU Census.</p>}
      {neonate && (
        <>
          {msg && <div className={`mb ${/discharged|saved/i.test(msg) ? 'info-banner' : 'error'}`}>{msg}</div>}
          <p className="hint mb">{doneCount}/{DISCHARGE_CRITERIA.length} criteria met for {neonate.babyName}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {DISCHARGE_CRITERIA.map((crit) => (
              <label key={crit} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', background: checks[crit] ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: `1px solid ${checks[crit] ? '#86efac' : '#e2e8f0'}`, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={checks[crit] || false} onChange={(e) => setChecks((c) => ({ ...c, [crit]: e.target.checked }))} />
                {crit}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save checklist'}</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || neonate.status === 'Discharged'}
              onClick={discharge}
              style={{ background: allChecked ? '#10b981' : undefined }}
            >
              {neonate.status === 'Discharged' ? '✓ Already Discharged' : saving ? 'Discharging…' : 'Discharge Neonate'}
            </button>
          </div>
          {!allChecked && neonate.status !== 'Discharged' && (
            <p className="hint" style={{ marginTop: 8, color: '#f59e0b' }}>{DISCHARGE_CRITERIA.length - doneCount} criteria still pending — discharge anyway with caution.</p>
          )}
        </>
      )}
    </div>
  );
}

export default function NicuPage() {
  const { records: neonates, create, patch, loading } = useHmsRecords('nicu-neonate');
  const { config, save: saveIncubators, loading: incLoading } = useHmsConfig('nicu-incubators');
  const [tab, setTab] = useState('census');
  const [selectedNeonate, setSelectedNeonate] = useState(null);
  const [savingInc, setSavingInc] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [regBusy, setRegBusy] = useState(false);
  const [regMsg, setRegMsg] = useState('');
  const [regForm, setRegForm] = useState({
    babyName: '', motherName: '', motherUhid: '', dob: new Date().toISOString().slice(0, 10),
    tob: '', gender: 'M', birthWeightGrams: '', gestationWeeks: '38', deliveryType: 'Normal',
    apgar1min: '8', apgar5min: '9', incubator: '', onCpap: false,
    admitReason: '', paediatrician: '', obstetrician: '',
  });

  const incubators = config?.incubators || [];

  useEffect(() => {
    if (neonates.length && !selectedNeonate) setSelectedNeonate(neonates[0]);
  }, [neonates, selectedNeonate]);

  const saveWeights = async (weights) => {
    if (!selectedNeonate) return;
    await patch(selectedNeonate.recordId || selectedNeonate.id, { weights });
  };

  const neoList = neonates.length ? neonates : [];

  const saveApgar = async (data) => {
    if (!selectedNeonate) return;
    await patch(selectedNeonate.recordId || selectedNeonate.id, data);
  };

  const handleSaveIncubators = async (list) => {
    setSavingInc(true);
    try {
      await saveIncubators({ incubators: list });
    } catch (e) {
      setRegMsg(e.message || 'Could not save incubators');
    } finally { setSavingInc(false); }
  };

  const registerNeonate = async (e) => {
    e.preventDefault();
    if (!regForm.babyName.trim() || !regForm.motherName.trim()) {
      setRegMsg('Baby name and mother name are required.');
      return;
    }
    setRegBusy(true);
    setRegMsg('');
    try {
      const created = await create({
        ...regForm,
        birthWeightGrams: Number(regForm.birthWeightGrams || 0),
        gestationWeeks: Number(regForm.gestationWeeks || 0),
        apgar1min: Number(regForm.apgar1min || 0),
        apgar5min: Number(regForm.apgar5min || 0),
        onCpap: !!regForm.onCpap,
        weights: [{ day: 0, weight: Number(regForm.birthWeightGrams || 0), feed: 'Colostrum', urine: true, stool: true }],
        status: 'Admitted',
        admittedAt: new Date().toISOString(),
      });
      if (regForm.incubator) {
        const next = incubators.map((inc) => (
          inc.id === regForm.incubator ? { ...inc, status: 'In Use', baby: regForm.babyName } : inc
        ));
        await saveIncubators({ incubators: next });
      }
      setShowRegister(false);
      setRegForm({
        babyName: '', motherName: '', motherUhid: '', dob: new Date().toISOString().slice(0, 10),
        tob: '', gender: 'M', birthWeightGrams: '', gestationWeeks: '38', deliveryType: 'Normal',
        apgar1min: '8', apgar5min: '9', incubator: '', onCpap: false,
        admitReason: '', paediatrician: '', obstetrician: '',
      });
      if (created) setSelectedNeonate(created);
      setRegMsg('Neonate registered.');
    } catch (err) {
      setRegMsg(err.message || 'Could not register neonate');
    } finally {
      setRegBusy(false);
    }
  };

  return (
    <PageShell
      title="NICU — Neonatal Intensive Care Unit"
      subtitle="Birth Registration · Apgar Score · Weight Chart · Incubator Management · Feeding Record"
      actions={(
        <button type="button" className="btn btn-primary" onClick={() => setShowRegister((s) => !s)}>
          {showRegister ? 'Cancel' : '+ Register Neonate'}
        </button>
      )}
    >
      {regMsg && <div className={`mb ${/fail|error|could/i.test(regMsg) ? 'error' : 'info-banner'}`}>{regMsg}</div>}
      {showRegister && (
        <form className="card mb" onSubmit={registerNeonate} style={{ borderLeft: '4px solid #6366f1' }}>
          <h3 style={{ marginTop: 0 }}>Register Neonate</h3>
          <div className="form-grid mb">
            <div className="field"><label>Baby name *</label><input className="input" required value={regForm.babyName} onChange={(e) => setRegForm({ ...regForm, babyName: e.target.value })} /></div>
            <div className="field"><label>Mother name *</label><input className="input" required value={regForm.motherName} onChange={(e) => setRegForm({ ...regForm, motherName: e.target.value })} /></div>
            <div className="field"><label>Mother UHID</label><input className="input" value={regForm.motherUhid} onChange={(e) => setRegForm({ ...regForm, motherUhid: e.target.value })} /></div>
            <div className="field"><label>DOB</label><input type="date" className="input" value={regForm.dob} onChange={(e) => setRegForm({ ...regForm, dob: e.target.value })} /></div>
            <div className="field"><label>Time of birth</label><input className="input" value={regForm.tob} onChange={(e) => setRegForm({ ...regForm, tob: e.target.value })} placeholder="HH:MM" /></div>
            <div className="field"><label>Gender</label><select className="input" value={regForm.gender} onChange={(e) => setRegForm({ ...regForm, gender: e.target.value })}><option value="M">Male</option><option value="F">Female</option></select></div>
            <div className="field"><label>Birth weight (g)</label><input type="number" className="input" value={regForm.birthWeightGrams} onChange={(e) => setRegForm({ ...regForm, birthWeightGrams: e.target.value })} /></div>
            <div className="field"><label>Gestation (weeks)</label><input type="number" className="input" value={regForm.gestationWeeks} onChange={(e) => setRegForm({ ...regForm, gestationWeeks: e.target.value })} /></div>
            <div className="field"><label>Delivery</label><select className="input" value={regForm.deliveryType} onChange={(e) => setRegForm({ ...regForm, deliveryType: e.target.value })}>{['Normal', 'LSCS', 'Assisted', 'Instrumental'].map((d) => <option key={d}>{d}</option>)}</select></div>
            <div className="field"><label>Apgar 1 min</label><input type="number" className="input" value={regForm.apgar1min} onChange={(e) => setRegForm({ ...regForm, apgar1min: e.target.value })} /></div>
            <div className="field"><label>Apgar 5 min</label><input type="number" className="input" value={regForm.apgar5min} onChange={(e) => setRegForm({ ...regForm, apgar5min: e.target.value })} /></div>
            <div className="field">
              <label>Incubator</label>
              <select className="input" value={regForm.incubator} onChange={(e) => setRegForm({ ...regForm, incubator: e.target.value })}>
                <option value="">— Optional —</option>
                {incubators.filter((i) => i.status === 'Available').map((i) => <option key={i.id} value={i.id}>{i.id} · {i.type}</option>)}
              </select>
            </div>
            <div className="field"><label><input type="checkbox" checked={regForm.onCpap} onChange={(e) => setRegForm({ ...regForm, onCpap: e.target.checked })} /> On CPAP</label></div>
            <div className="field"><label>Admit reason</label><input className="input" value={regForm.admitReason} onChange={(e) => setRegForm({ ...regForm, admitReason: e.target.value })} placeholder="e.g. Preterm, RDS" /></div>
            <div className="field"><label>Paediatrician</label><input className="input" value={regForm.paediatrician} onChange={(e) => setRegForm({ ...regForm, paediatrician: e.target.value })} /></div>
            <div className="field"><label>Obstetrician</label><input className="input" value={regForm.obstetrician} onChange={(e) => setRegForm({ ...regForm, obstetrician: e.target.value })} /></div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={regBusy}>{regBusy ? 'Saving…' : 'Save Neonate'}</button>
        </form>
      )}
      {!incLoading && incubators.length === 0 && (
        <IncubatorsSetup onSave={handleSaveIncubators} busy={savingInc} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Neonates', val: neoList.length, color: '#6366f1' }, { label: 'Incubators In Use', val: incubators.filter(i => i.status === 'In Use').length, color: '#f59e0b' }, { label: 'Available', val: incubators.filter(i => i.status === 'Available').length, color: '#10b981' }, { label: 'On CPAP', val: neoList.filter((n) => n.onCpap).length, color: '#3b82f6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'census', label: '👶 NICU Census' },
          { id: 'apgar', label: '📊 Apgar Calculator' },
          { id: 'weight', label: '📈 Weight Chart' },
          { id: 'phototherapy', label: '☀ Phototherapy' },
          { id: 'nbs', label: '🧪 NBS Screening' },
          { id: 'discharge', label: '✅ Discharge' },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'census' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 12 }}>
          {neoList.length === 0 && !loading && <p className="hint table-empty full">No neonates admitted. Click “+ Register Neonate” to add the first baby.</p>}
          {neoList.map(neo => (
            <div key={neo.id || neo.recordId} className="card" style={{ borderLeft: '4px solid #6366f1', cursor: 'pointer', border: selectedNeonate?.id === neo.id || selectedNeonate?.recordId === neo.recordId ? '2px solid #6366f1' : undefined }} onClick={() => setSelectedNeonate(neo)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 900, fontSize: 15 }}>👶 {neo.babyName}</div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: neo.gestationWeeks < 35 ? '#fef2f2' : '#fffbeb', color: neo.gestationWeeks < 35 ? '#dc2626' : '#92400e', border: `1px solid ${neo.gestationWeeks < 35 ? '#fca5a5' : '#fde68a'}` }}>{neo.gestationWeeks} wks</span>
              </div>
              <div style={{ fontSize: 12, color: '#475569' }}>Mother: <strong>{neo.motherName}</strong> ({neo.motherUhid})</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8, fontSize: 12 }}>
                {[['DOB/TOB', `${neo.dob} ${neo.tob}`], ['Gender', neo.gender === 'M' ? '♂ Male' : '♀ Female'], ['Birth Weight', `${neo.birthWeightGrams}g`], ['Delivery', neo.deliveryType], ['Apgar 1/5', `${neo.apgar1min}/${neo.apgar5min}`], ['Incubator', neo.incubator]].map(([label, val]) => (
                  <div key={label} style={{ padding: '4px 8px', background: '#f8fafc', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{label}</div>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Reason: {neo.admitReason || '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Paed: {neo.paediatrician || '—'} · Obs: {neo.obstetrician || '—'}</div>
            </div>
          ))}
          <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
            <h4 style={{ marginTop: 0, color: '#10b981' }}>🌡 Incubator / Warmer Status</h4>
            {incubators.length === 0 ? (
              <p className="hint">Configure incubators above.</p>
            ) : incubators.map(inc => (
              <div key={inc.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: inc.status === 'In Use' ? '#fffbeb' : '#f0fdf4', border: `1px solid ${inc.status === 'In Use' ? '#fde68a' : '#86efac'}`, marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{inc.id} — {inc.type}</div>
                  {inc.baby && <div style={{ fontSize: 11, color: '#64748b' }}>Baby: {inc.baby} · {inc.temp}°C · {inc.humidity}% RH</div>}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: inc.status === 'In Use' ? '#fef9c3' : '#dcfce7', color: inc.status === 'In Use' ? '#92400e' : '#16a34a' }}>{inc.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'apgar' && <ApgarCalculator neonate={selectedNeonate} onSave={saveApgar} />}
      {tab === 'weight' && <WeightChartTab neonate={selectedNeonate} onSaveWeights={saveWeights} />}
      {tab === 'phototherapy' && (
        <PhototherapyTab
          neonate={selectedNeonate}
          onSave={async (fields) => {
            if (!selectedNeonate) return;
            await patch(selectedNeonate.recordId || selectedNeonate.id, fields);
          }}
        />
      )}
      {tab === 'nbs' && (
        <NbsScreeningTab
          neonate={selectedNeonate}
          onSave={async (fields) => {
            if (!selectedNeonate) return;
            await patch(selectedNeonate.recordId || selectedNeonate.id, fields);
          }}
        />
      )}
      {tab === 'discharge' && (
        <DischargeChecklistTab
          neonate={selectedNeonate}
          incubators={incubators}
          onSave={async (fields) => {
            if (!selectedNeonate) return;
            await patch(selectedNeonate.recordId || selectedNeonate.id, fields);
          }}
          onSaveIncubators={saveIncubators}
        />
      )}
    </PageShell>
  );
}
